import React, { useState, useEffect } from 'react';
import { FaBuilding, FaGlobe, FaClock, FaCheckCircle, FaTimesCircle, FaHourglassHalf, FaArrowLeft } from 'react-icons/fa';
import './PartnershipApplications.css';
import { getAuth } from 'firebase/auth';
import { getCountryName } from '../../utils/locationUtils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getFunctions } from 'firebase/functions';
import { partnershipCache } from '../../utils/cache';
import { partnershipAPI } from '../../services/api';
// Add date formatting utility
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

function PartnershipApplications() {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const functions = getFunctions();

  const fetchApplications = async () => {
    try {
      // Check cache first
      const cachedApplications = partnershipCache.get('applications');
      if (cachedApplications) {
        setApplications(cachedApplications);
        setFilteredApplications(cachedApplications);
        setLoading(false);
        return;
      }

      const result = await partnershipAPI.getAllApplications();
      
      // The API returns an object with applications array directly in the response
      const applicationsData = result?.applications || [];
      
      // Update cache
      partnershipCache.set('applications', applicationsData);
      
      setApplications(applicationsData);
      setFilteredApplications(applicationsData);
    } catch (error) {
      setError('Failed to load applications');
      setApplications([]);
      setFilteredApplications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const updateLastViewed = async () => {
      try {
        await partnershipAPI.updateNotifications('partnership_application');
      } catch (error) {
      }
    };

    // Get user role from token
    const getUserRole = async () => {
      try {
        const token = await user.getIdTokenResult();
        const userRole = token.claims?.role || 'user';
        setUserRole(userRole);
      } catch (error) {
      }
    };

    getUserRole();
    updateLastViewed();
    fetchApplications();
  }, []);

  // Add useEffect to filter applications when statusFilter changes
  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredApplications(applications || []);
    } else {
      setFilteredApplications((applications || []).filter(app => app.status === statusFilter));
    }
  }, [statusFilter, applications]);

  // Clear cache when component unmounts
  useEffect(() => {
    return () => {
      partnershipCache.delete('applications');
    };
  }, []);

  const handleViewDetails = (applicationId) => {
    if (!applications) return;
    const application = applications.find(app => app.id === applicationId);
    setSelectedApplication(application);
    setIsDetailsModalOpen(true);
  };

  const handleUpdateStatus = (applicationId) => {
    if (!applications) return;
    const application = applications.find(app => app.id === applicationId);
    if (!application) {
      throw new Error('Application not found');
    }
    setSelectedApplication(application);
    setIsStatusModalOpen(true);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      if (!selectedApplication) {
        throw new Error('No application selected');
      }

      await partnershipAPI.updateStatus(selectedApplication.id, newStatus);
      
      // Invalidate cache to force fresh data
      partnershipCache.delete('applications');
      
      // Refresh the applications list
      await fetchApplications();
      
      // Close the modal and clear selection
      setIsStatusModalOpen(false);
      setSelectedApplication(null);
      
      // Show success message
      alert('Application status updated successfully');
    } catch (error) {
      setError('Failed to update application status');
    }
  };

  const handleDeleteApplication = async () => {
    if (!selectedApplication) return;
    
    if (window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      try {
        await partnershipAPI.deletePartnershipApplication(selectedApplication.id);

        // It's generally better to refetch data before updating local UI state like closing modals
        await fetchApplications(); // Refresh the applications list
        
        setIsStatusModalOpen(false);
        setSelectedApplication(null);
      } catch (error) {
        setError('Failed to delete application');
      }
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved':
        return <FaCheckCircle className="status-icon approved" />;
      case 'rejected':
        return <FaTimesCircle className="status-icon rejected" />;
      default:
        return <FaHourglassHalf className="status-icon pending" />;
    }
  };

  const DetailsModal = ({ application, onClose }) => {
    if (!application) return null;

    return (
      <div className="partnership-modal-overlay">
        <div className="partnership-modal-content">
          <h2>Application Details</h2>
          <div className="partnership-detail-section">
            <h3>Organization Information</h3>
            <p><strong>Organization Name:</strong> {application.organizationName}</p>
            <p><strong>Organization Type:</strong> {application.organizationType}</p>
            <p><strong>Website:</strong> {application.website || 'Not provided'}</p>
            <p><strong>Country:</strong> {getCountryName(application.country)}</p>
            <div className="partnership-message-section">
              <strong>Organization Description:</strong>
              <p>{application.description}</p>
            </div>
          </div>

          <div className="partnership-detail-section">
            <h3>Contact Information</h3>
            <p><strong>Contact Person:</strong> {application.contactPerson}</p>
            <p><strong>Email:</strong> {application.email}</p>
            <p><strong>Phone:</strong> {application.phone || 'Not provided'}</p>
          </div>

          <div className="partnership-detail-section">
            <h3>Partnership Information</h3>
            <div className="partnership-message-section">
              <strong>Partnership Goals:</strong>
              <p>{application.partnershipGoals}</p>
            </div>
          </div>

          <div className="partnership-detail-section">
            <h3>Application Status</h3>
            <p><strong>Status:</strong> <span className={`partnership-status-badge ${application.status || 'pending'}`}>
              {application.status || 'pending'}
            </span></p>
            <p><strong>Submitted:</strong> {formatDate(application.submissionDate)}</p>
          </div>
          <button className="partnership-close-button" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  };

  const StatusModal = ({ application, onClose }) => {
    if (!application) return null;

    // Check if user is super admin
    const isSuperAdmin = userRole === 'superAdmin';

    return (
      <div className="partnership-modal-overlay">
        <div className="partnership-modal-content">
          <h2>Update Status</h2>
          <div className="partnership-status-buttons">
            <button 
              className={`partnership-status-button pending ${application.status === 'pending' ? 'active' : ''}`}
              onClick={() => handleStatusChange('pending')}
            >
              Pending
            </button>
            <button 
              className={`partnership-status-button approved ${application.status === 'approved' ? 'active' : ''}`}
              onClick={() => handleStatusChange('approved')}
            >
              Approved
            </button>
            <button 
              className={`partnership-status-button rejected ${application.status === 'rejected' ? 'active' : ''}`}
              onClick={() => handleStatusChange('rejected')}
            >
              Rejected
            </button>
          </div>
          <div className="partnership-modal-actions">
            {isSuperAdmin && (
              <button className="partnership-delete-button" onClick={handleDeleteApplication}>
                Delete Application
              </button>
            )}
            <button className="partnership-close-button" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="partnership-loading-message">Loading applications...</div>;
  }

  if (error) {
    return (
      <div className="partnership-error-message">
        <h3>Error loading applications</h3>
        <p>{error}</p>
        <button onClick={fetchApplications}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="partnership-applications-wrapper">
      <div className="partnership-applications-header">
        <button className="partnership-back-button" onClick={() => navigate('/admin')}>
          <FaArrowLeft /> Back to Dashboard
        </button>
        <div className="partnership-header-content">
          <h2 className="partnership-header-title">Partnership Applications</h2>
          <div className="partnership-filter-container">
            <label htmlFor="statusFilter">Filter by Status:</label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="partnership-filter-select"
            >
              <option value="all">All Applications</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
        <div className="partnership-stats-dashboard">
          <div className="partnership-stat-item">
            <span className="partnership-stat-number">{applications?.length || 0}</span>
            <span className="partnership-stat-label">Total Applications</span>
          </div>
          <div className="partnership-stat-item">
            <span className="partnership-stat-number">
              {applications?.filter(app => app.status === 'pending')?.length || 0}
            </span>
            <span className="partnership-stat-label">Pending</span>
          </div>
          <div className="partnership-stat-item">
            <span className="partnership-stat-number">
              {applications?.filter(app => app.status === 'approved')?.length || 0}
            </span>
            <span className="partnership-stat-label">Approved</span>
          </div>
        </div>
      </div>

      <div className="partnership-applications-grid">
        {filteredApplications.length === 0 ? (
          <div className="partnership-no-applications">
            <FaBuilding size={48} opacity={0.3} />
            <p>No partnership applications {statusFilter !== 'all' ? `with status "${statusFilter}"` : ''} found</p>
          </div>
        ) : (
          filteredApplications.map((application) => (
            <div key={application.id} className="partnership-application-card">
              <div className="partnership-card-header">
                <div className="partnership-org-info">
                  <h3>{application.organizationName}</h3>
                  <span className="partnership-org-type">{application.organizationType}</span>
                </div>
                {getStatusIcon(application.status)}
              </div>
              
              <div className="partnership-card-body">
                <div className="partnership-info-row">
                  <FaBuilding className="partnership-info-icon" />
                  <span>{getCountryName(application.country)}</span>
                </div>
                <div className="partnership-info-row">
                  <FaGlobe className="partnership-info-icon" />
                  <span>{application.website || 'No website provided'}</span>
                </div>
                <div className="partnership-info-row">
                  <FaClock className="partnership-info-icon" />
                  <span>{formatDate(application.submissionDate)}</span>
                </div>
              </div>

              <div className="partnership-card-actions">
                <button 
                  className="partnership-action-button view"
                  onClick={() => handleViewDetails(application.id)}
                >
                  View Details
                </button>
                <button 
                  className="partnership-action-button status"
                  onClick={() => handleUpdateStatus(application.id)}
                >
                  Update Status
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      {isDetailsModalOpen && (
        <DetailsModal 
          application={selectedApplication} 
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedApplication(null);
          }}
        />
      )}
      
      {isStatusModalOpen && (
        <StatusModal 
          application={selectedApplication} 
          onClose={() => {
            setIsStatusModalOpen(false);
            setSelectedApplication(null);
          }}
        />
      )}
    </div>
  );
}

export default PartnershipApplications;
